import { Injectable, inject } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationStart, Router } from '@angular/router';

/** Dónde caer cuando el visor se abrió directo y no hay nada atrás. */
const RUTA_DE_SALIDA = '/home';

/**
 * El "volver" del visor: retrocede en el historial, como la flecha del
 * navegador.
 *
 * Antes las flechas apuntaban al launcher con un `routerLink` fijo, así que
 * salir del visor te dejaba en una pantalla que no era de donde habías
 * entrado. Ahora vuelven a la vista anterior, sea la ficha del paciente, el
 * historial o la orden.
 *
 * Se cuenta el historial propio en vez de confiar en `history.length`, que
 * incluye lo que el usuario visitó antes de entrar a la app: retroceder ahí
 * lo sacaría del sistema. Cuando no hay entrada previa nuestra —el visor
 * abierto por link directo o desde la app nativa— se cae a {@link
 * RUTA_DE_SALIDA} en lugar de dejar el botón muerto.
 *
 * Se instancia al arrancar la app (ver `app.config.ts`): tiene que ver las
 * navegaciones desde la primera, no desde que el visor lo inyecta.
 */
@Injectable({ providedIn: 'root' })
export class ViewerBackService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  /**
   * Entradas propias en la pila del historial.
   *
   * Solo suman las navegaciones que empujan una entrada: un `replaceUrl`
   * —la pantalla puente `/viewer/order/:id`— pisa la actual en vez de
   * agregarse, y un popstate resta la que se acaba de dejar.
   */
  private entradas = 0;

  constructor() {
    // La navegación inicial puede haber arrancado antes que este servicio
    // —withEnabledBlockingInitialNavigation corre en el injector de entorno,
    // antes que los app initializers—, y entonces su evento ya pasó. Se
    // cuenta acá para que el total no dependa de quién arrancó primero.
    if (this.router.getCurrentNavigation() || this.router.lastSuccessfulNavigation()) {
      this.entradas = 1;
    }

    this.router.events.subscribe((evento) => {
      if (!(evento instanceof NavigationStart)) return;
      const navegacion = this.router.getCurrentNavigation();
      if (navegacion?.trigger === 'popstate') {
        this.entradas = Math.max(1, this.entradas - 1);
      } else if (!navegacion?.extras.replaceUrl) {
        this.entradas++;
      }
    });
  }

  /** ¿Hay una vista anterior nuestra a la que volver? */
  get hayVistaAnterior(): boolean {
    return this.entradas > 1;
  }

  volver(): void {
    if (this.hayVistaAnterior) this.location.back();
    else void this.router.navigateByUrl(RUTA_DE_SALIDA);
  }
}
